import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import {
  FaExternalLinkAlt,
  FaFilePdf,
  FaImage,
  FaLink,
  FaVideo,
  FaYoutube,
} from "react-icons/fa";

import {
  toast,
} from "react-toastify";

import EditorDrawerShell from "./EditorDrawerShell";

import {
  Field,
  FormSection,
  Label,
  ToggleCard,
  TUTORIAL_INPUT_CLASS,
} from "./TutorialDrawerFields";

import {
  TUTORIAL_MEDIA_TYPE_OPTIONS,
} from "../constants/tutorialAdminOptions";

import {
  resolveAdminTutorialMediaUrl,
} from "../utils/tutorialAdminMediaUtils";

const INITIAL_FORM = {
  type_media: "image",
  url: "",
  miniature_url: "",
  alt_text: "",
  legende:
    "Touchez pour agrandir",
  ordre: 10,
  is_active: true,
};

function createInitialForm(
  drawer
) {
  if (
    drawer.mode === "edit" &&
    drawer.data
  ) {
    return {
      type_media:
        drawer.data.type_media ||
        "image",

      url:
        drawer.data.url || "",

      miniature_url:
        drawer.data
          .miniature_url || "",

      alt_text:
        drawer.data.alt_text || "",

      legende:
        drawer.data.legende || "",

      ordre:
        drawer.data.ordre ?? 10,

      is_active:
        drawer.data.is_active !==
        false,
    };
  }

  return {
    ...INITIAL_FORM,

    ordre:
      drawer.context
        ?.defaultOrder ?? 10,
  };
}

export default function TutorialMediaDrawer({
  drawer,
  saving,
  onClose,
  onSave,
}) {
  const isOpen =
    drawer.open &&
    drawer.type === "media";

  const [formData, setFormData] =
    useState(INITIAL_FORM);

  const [
    previewError,
    setPreviewError,
  ] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setFormData(
      createInitialForm(drawer)
    );

    setPreviewError(false);
  }, [
    isOpen,
    drawer.mode,
    drawer.data,
    drawer.context,
  ]);

  const previewUrl = useMemo(
    () =>
      resolveAdminTutorialMediaUrl(
        formData.miniature_url ||
          formData.url
      ),
    [
      formData.miniature_url,
      formData.url,
    ]
  );

  useEffect(() => {
    setPreviewError(false);
  }, [previewUrl]);

  const handleChange = (event) => {
    const {
      name,
      value,
      type,
      checked,
    } = event.target;

    setFormData((current) => ({
      ...current,

      [name]:
        type === "checkbox"
          ? checked
          : value,
    }));
  };

  const handleSubmit = async (
    event
  ) => {
    event.preventDefault();

    if (!formData.url.trim()) {
      toast.error(
        "L’URL du média est obligatoire."
      );
      return;
    }

    const order = Number.parseInt(
      formData.ordre,
      10
    );

    if (
      !Number.isInteger(order) ||
      order < 0
    ) {
      toast.error(
        "L’ordre du média est invalide."
      );
      return;
    }

    await onSave({
      type_media:
        formData.type_media,

      url:
        formData.url.trim(),

      miniature_url:
        formData.miniature_url
          .trim() || null,

      alt_text:
        formData.alt_text
          .trim() || null,

      legende:
        formData.legende
          .trim() || null,

      ordre: order,

      is_active:
        formData.is_active,
    });
  };

  const selectedType =
    TUTORIAL_MEDIA_TYPE_OPTIONS.find(
      (option) =>
        option.value ===
        formData.type_media
    );

  return (
    <EditorDrawerShell
      open={isOpen}
      eyebrow={
        drawer.mode === "edit"
          ? "Modification"
          : "Nouveau média"
      }
      title={
        drawer.mode === "edit"
          ? "Modifier le média"
          : "Ajouter un média"
      }
      description={
        drawer.context?.stepTitle
          ? `Média associé à l’étape « ${drawer.context.stepTitle} ».`
          : "Ajoute une image, une vidéo, un PDF ou un lien à cette étape."
      }
      icon={<FaImage />}
      saving={saving}
      submitLabel={
        drawer.mode === "edit"
          ? "Enregistrer"
          : "Ajouter le média"
      }
      onClose={onClose}
      onSubmit={handleSubmit}
    >
      <div className="space-y-6">
        <FormSection
          title="Type et adresse"
          description="Le fichier doit actuellement être accessible depuis une URL."
        >
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <Field>
              <Label required>
                Type de média
              </Label>

              <select
                className={
                  TUTORIAL_INPUT_CLASS
                }
                name="type_media"
                value={
                  formData.type_media
                }
                onChange={handleChange}
              >
                {TUTORIAL_MEDIA_TYPE_OPTIONS.map(
                  (option) => (
                    <option
                      key={option.value}
                      value={
                        option.value
                      }
                    >
                      {option.label}
                    </option>
                  )
                )}
              </select>
            </Field>

            <Field>
              <Label>Ordre</Label>

              <input
                type="number"
                min="0"
                step="10"
                className={
                  TUTORIAL_INPUT_CLASS
                }
                name="ordre"
                value={formData.ordre}
                onChange={handleChange}
              />
            </Field>

            <Field className="md:col-span-2">
              <Label required>
                URL du média
              </Label>

              <input
                type="text"
                className={
                  TUTORIAL_INPUT_CLASS
                }
                name="url"
                value={formData.url}
                onChange={handleChange}
                placeholder={
                  formData.type_media ===
                  "image"
                    ? "https://.../capture.webp"
                    : "https://..."
                }
              />

              <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
                Utilise de préférence
                une URL absolue commençant
                par https://.
              </p>
            </Field>

            <Field className="md:col-span-2">
              <Label>
                URL de la miniature
              </Label>

              <input
                type="text"
                className={
                  TUTORIAL_INPUT_CLASS
                }
                name="miniature_url"
                value={
                  formData.miniature_url
                }
                onChange={handleChange}
                placeholder="Facultatif"
              />
            </Field>
          </div>
        </FormSection>

        <FormSection
          title="Accessibilité et affichage"
          description="Ces informations sont affichées dans le tutoriel public."
        >
          <div className="grid grid-cols-1 gap-4">
            <Field>
              <Label>
                Texte alternatif
              </Label>

              <input
                type="text"
                maxLength="255"
                className={
                  TUTORIAL_INPUT_CLASS
                }
                name="alt_text"
                value={
                  formData.alt_text
                }
                onChange={handleChange}
                placeholder="Exemple : formulaire de création d’un compte"
              />
            </Field>

            <Field>
              <Label>Légende</Label>

              <textarea
                maxLength="500"
                className={`${TUTORIAL_INPUT_CLASS} min-h-[110px] resize-y leading-6`}
                name="legende"
                value={
                  formData.legende
                }
                onChange={handleChange}
                placeholder="Touchez pour agrandir"
              />
            </Field>
          </div>

          <div className="mt-5">
            <ToggleCard
              name="is_active"
              checked={
                formData.is_active
              }
              onChange={handleChange}
              title="Média actif"
              description="Autorise l’affichage de ce média dans le tutoriel public."
            />
          </div>
        </FormSection>

        <FormSection
          title="Aperçu"
          description={`Type sélectionné : ${
            selectedType?.label ||
            formData.type_media
          }.`}
        >
          <MediaPreview
            type={
              formData.type_media
            }
            url={previewUrl}
            alt={
              formData.alt_text ||
              drawer.context
                ?.stepTitle ||
              "Aperçu du média"
            }
            hasError={previewError}
            onError={() =>
              setPreviewError(true)
            }
          />
        </FormSection>
      </div>
    </EditorDrawerShell>
  );
}

function MediaPreview({
  type,
  url,
  alt,
  hasError,
  onError,
}) {
  if (!url) {
    return (
      <PreviewEmpty
        icon={<FaImage />}
        message="Saisis une URL pour afficher un aperçu."
      />
    );
  }

  if (type === "image") {
    if (hasError) {
      return (
        <PreviewEmpty
          icon={<FaImage />}
          message="Impossible de charger cette image. Vérifie son URL."
          error
        />
      );
    }

    return (
      <div className="flex min-h-[240px] items-center justify-center overflow-hidden rounded-[22px] border border-slate-200 bg-slate-100 p-3 dark:border-slate-700 dark:bg-slate-950">
        <img
          src={url}
          alt={alt}
          onError={onError}
          className="max-h-[460px] w-full rounded-2xl object-contain"
        />
      </div>
    );
  }

  const config = {
    youtube: {
      icon: <FaYoutube />,
      label: "Vidéo YouTube",
      color:
        "bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300",
    },

    video: {
      icon: <FaVideo />,
      label: "Fichier vidéo",
      color:
        "bg-sky-100 text-sky-700 dark:bg-sky-500/15 dark:text-sky-300",
    },

    pdf: {
      icon: <FaFilePdf />,
      label: "Document PDF",
      color:
        "bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300",
    },

    lien: {
      icon: <FaLink />,
      label: "Lien externe",
      color:
        "bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-300",
    },
  };

  const selected =
    config[type] || config.lien;

  return (
    <a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="flex items-center gap-4 rounded-[22px] border border-slate-200 bg-slate-50 p-5 transition hover:border-violet-300 dark:border-slate-700 dark:bg-slate-800/50"
    >
      <div
        className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl text-xl ${selected.color}`}
      >
        {selected.icon}
      </div>

      <div className="min-w-0 flex-1">
        <div className="font-semibold text-slate-900 dark:text-white">
          {selected.label}
        </div>

        <div className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400">
          {url}
        </div>
      </div>

      <FaExternalLinkAlt className="shrink-0 text-slate-400" />
    </a>
  );
}

function PreviewEmpty({
  icon,
  message,
  error = false,
}) {
  return (
    <div
      className={`flex min-h-[200px] items-center justify-center rounded-[22px] border border-dashed p-6 text-center ${
        error
          ? "border-rose-300 bg-rose-50 text-rose-600 dark:border-rose-900/50 dark:bg-rose-950/20 dark:text-rose-300"
          : "border-slate-300 bg-slate-50 text-slate-500 dark:border-slate-700 dark:bg-slate-800/40 dark:text-slate-400"
      }`}
    >
      <div>
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-xl shadow-sm dark:bg-slate-900">
          {icon}
        </div>

        <p className="mt-3 text-sm">
          {message}
        </p>
      </div>
    </div>
  );
}